import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  ANNUITY_DEATH_BENEFIT_OPTIONS,
  ANNUITY_LIVING_BENEFIT_OPTIONS,
  FormFields,
} from '../../AccountForm.meta';
import { Select } from '../../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { InputNumberMask } from '../../../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { useAnnuityRiders } from './useAnnuityRiders';

export const AnnuityRiders = () => {
  const { control, watch, setValue } = useFormContext<FormFields>();

  const [isAnnuityLivingBenefitRider, isAnnuityDeathBenefitRider] = watch([
    'bankAccount.AnnuityHasLivingBenefitRider',
    'bankAccount.AnnuityHasDeathBenefitRider',
  ]);

  const { incomeStartYearOptions } = useAnnuityRiders();

  return (
    <>
      <Controller
        control={control}
        name="bankAccount.AnnuityHasLivingBenefitRider"
        render={({ field: { value, onChange, ...field } }) => {
          return (
            <Select
              {...field}
              options={ANNUITY_LIVING_BENEFIT_OPTIONS}
              value={ANNUITY_LIVING_BENEFIT_OPTIONS.find(option => option.value === value)}
              label="Living Benefit"
              isPortal
              onChange={opt => {
                onChange(opt?.value);
              }}
            />
          );
        }}
      />

      {isAnnuityLivingBenefitRider && (
        <>
          <Controller
            control={control}
            name="bankAccount.AnnuityLivingBenefitRider.CurrentBenefitBase"
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                {...field}
                onValueChange={({ value }) => onChange(value ?? null)}
                leftIcon="fa fa-dollar-sign"
                allowNegative={false}
                label="Current Benefit Base"
                tooltip="The benefit base is used to calculate the guaranteed minimum income"
              />
            )}
          />

          <Controller
            control={control}
            name="bankAccount.AnnuityLivingBenefitRider.RollUpRate"
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                {...field}
                onValueChange={({ value }) => onChange(value ?? null)}
                rightIcon="far fa-percent"
                decimalScale={2}
                allowNegative={false}
                thousandSeparator={false}
                label="Roll-up Rate"
                tooltip="The rate at which the benefit base grows"
              />
            )}
          />

          <Controller
            control={control}
            name="bankAccount.AnnuityLivingBenefitRider.RollUpPeriod"
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                {...field}
                onValueChange={({ value }) => onChange(value ?? null)}
                innerText="years"
                label="Period Duration"
              />
            )}
          />

          <Controller
            control={control}
            name="bankAccount.AnnuityLivingBenefitRider.WithdrawalBenefit"
            render={({ field }) => (
              <InputNumberMask
                {...field}
                rightIcon="far fa-percent"
                decimalScale={2}
                allowNegative={false}
                thousandSeparator={false}
                label="Withdrawal Benefit"
                tooltip="The percentage of the benefit base that is withdrawn as income"
              />
            )}
          />

          <Controller
            control={control}
            name="bankAccount.AnnuityLivingBenefitRider.IncomeStartYear"
            render={({ field: { value, onChange, ...field } }) => {
              return (
                <Select
                  {...field}
                  options={incomeStartYearOptions}
                  value={incomeStartYearOptions.find(option => option.value === value)}
                  label="Income Start Year"
                  isPortal
                  onChange={opt => {
                    onChange(opt?.value);

                    setValue(
                      'bankAccount.AnnuityLivingBenefitRider.IncomeStartAtRetirement',
                      opt?.value === -1,
                    );
                  }}
                />
              );
            }}
          />
        </>
      )}

      <Controller
        control={control}
        name="bankAccount.AnnuityHasDeathBenefitRider"
        render={({ field: { value, onChange, ...field } }) => {
          return (
            <Select
              {...field}
              options={ANNUITY_DEATH_BENEFIT_OPTIONS}
              value={ANNUITY_DEATH_BENEFIT_OPTIONS.find(option => option.value === value)}
              label="Death Benefit"
              isPortal
              onChange={opt => {
                onChange(opt?.value);
              }}
            />
          );
        }}
      />

      {isAnnuityDeathBenefitRider && (
        <Controller
          control={control}
          name="bankAccount.AnnuityDeathBenefitRiderAmount"
          render={({ field: { onChange, ...field } }) => (
            <InputNumberMask
              {...field}
              leftIcon="fa fa-dollar-sign"
              allowNegative={false}
              label="Death Benefit Amount"
              onValueChange={({ value }) => {
                onChange(value ?? null);
              }}
            />
          )}
        />
      )}
    </>
  );
};
